import React from 'react'
import { SEO } from 'newComponents'
import Layout from '../components/layout'
import CtaArrow from '../components/ctaArrow'
import ClosingCTAs from '../components/ClosingCTAs'

import auditLog from '../images/about-feature-management/audit-log-laptop.svg'
import engTeams from '../images/benefits/engineering-teams.svg'
import toggle from '../images/benefits/Toggle_v3.svg'
import actionTree from '../images/benefits/ActionTree_v2.svg'
import toggle2 from '../images/benefits/Toggle_v2.svg'
import clock from '../images/benefits/Clock_v1.svg'
import productTeams from '../images/benefits/product-teams.svg'
import clock2 from '../images/benefits/Clock_v2.svg'
import feedback from '../images/illustrations/small/feedback.png'
import toggle5 from '../images/benefits/Toggle_v5.svg'
import appDirect from '../images/case-studies/appdirect-logo-light-large.png'
import quotes from '../images/quotes.svg'

const Benefits = () => (
  <Layout>
    <SEO
      title="Benefits"
      description="Starting to feature flag is easy, managing them at scale is hard. See how your team can use LaunchDarkly's feature management platform to move faster with less risk—separate code deployments from feature releases, safely test in production, and use feature flags as a kill switch."
    />
    <div className="clipped-container clipped-positioning">
      <section className="section is-large bg-gradient-reverse is-clipped clipped-short" />
    </div>
    <section className="section is-large">
      <div className="container content hero">
        <div className="columns is-multiline">
          <div className="column is-12 is-narrow has-text-centered-tablet">
            <h1 className="title">Unleash the Full Potential of Your Team</h1>
            <p className="text-large">
              LaunchDarkly's feature management platform allows you to separate
              code deployments from feature releases, quickly gain valuable
              feedback and continue to build a better product for your users.
            </p>
          </div>
          <div className="column is-12 has-text-centered-tablet">
            <img
              src={auditLog}
              alt="audit log on laptop"
              height="auto"
              width="878"
            />
          </div>
        </div>
      </div>
    </section>
    <div className="clipped-container">
      <section className="section bg-dark bg-static-stars is-large is-clipped">
        <div className="container content">
          <div className="columns">
            <div className="column">
              <h2 className="title">Engineering Teams</h2>
              <p>
                Developers and operations teams use LaunchDarkly to separate
                code deployments from feature releases. Companies like Atlassian
                and Microsoft use our feature management platform to support
                effective modern development cycles—using feature flags to
                deploy faster, control releases, and get more feedback from end
                users.
              </p>
            </div>
            <div className="column has-text-centered-tablet">
              <img
                className="xlarge-icon benefits"
                src={engTeams}
                alt="engineering teams"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
    <section className="section is-large bg-gradient">
      <div className="container content">
        <div className="section-title">
          <h2 className="title">
            Separate Your Code Deployment from Your Feature Releases
          </h2>
          <p className="subtitle">
            Rethink your workflow. Tools that will help you move as the speed of
            the market.
          </p>
        </div>
        <div className="columns is-multiline features">
          <div className="column is-half is-mobile featureList-item columns is-mobile">
            <div className="column is-one-fifth has-text-right-tablet">
              <img className="icon" src={toggle} alt="toggle" />
            </div>
            <div className="column is-four-fifths">
              <h4>Test in Production</h4>
              <p>
                Test early, test often, and do it in your production
                environment. Use custom targeting rules and percentage rollouts
                to safely test features in production and get valuable feedback.
              </p>
              <p>
                Using feature management as a best practice, your teams can
                safely get feedback from users and iterate faster.
              </p>
            </div>
          </div>
          <div className="column is-half is-mobile featureList-item columns is-mobile">
            <div className="column is-one-fifth has-text-right-tablet">
              <img className="icon" src={actionTree} alt="action tree" />
            </div>
            <div className="column is-four-fifths">
              <h4>Control Version Output</h4>
              <p>
                Deploy your code when you want to and then let product teams
                independently release features when they are ready.
              </p>
              <p>
                LaunchDarkly gives you granular control over your user's
                experience. With feature flags, you don't have to roll back code
                to un-do a feature release—simply change the flag status.
              </p>
            </div>
          </div>
          <div className="column is-half is-mobile featureList-item columns is-mobile">
            <div className="column is-one-fifth has-text-right-tablet">
              <img className="icon" src={toggle2} alt="toggle" />
            </div>
            <div className="column is-four-fifths">
              <h4>Central Visibility and Control</h4>
              <p>
                One dashboard to manage the lifecycle of your features from
                local development, to QA, to production.
              </p>
              <p>
                Manage multiple different software projects with their own
                development environments. Create flexible policies that provide
                fine-grained access control. And have real-time visibility into
                every feature flag change made in your system.
              </p>
            </div>
          </div>
          <div className="column is-half is-mobile featureList-item columns is-mobile">
            <div className="column is-one-fifth has-text-right-tablet">
              <img className="icon" src={clock} alt="clock" />
            </div>
            <div className="column is-four-fifths">
              <h4>Ship Code When You're Ready</h4>
              <p>
                Empower your teams to collaborate more effectively, maintain
                stability, and deliver software faster.
              </p>
              <p>
                Using feature management as a best practice, you can safely
                separate code deployments from feature releases, giving teams
                the flexibility to work on their own timelines.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
    <div className="clipped-container">
      <section className="section is-large bg-dark bg-static-stars is-clipped">
        <div className="container content">
          <div className="columns">
            <div className="column">
              <h2 className="title">Product Teams</h2>
              <p>
                Product teams use LaunchDarkly to release features when they
                want, independent of code deployments. With our feature
                management platform, these teams use custom targeting rules and
                percentage rollouts to maintain granular control over user
                experience. They also safely test in production and get valuable
                feedback faster.
              </p>
            </div>
            <div className="column has-text-centered-tablet">
              <img
                className="xlarge-icon"
                src={productTeams}
                alt="product Teams"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
    <section className="section is-large bg-gradient">
      <div className="container content">
        <div className="section-title">
          <h2 className="title">Give Your Users What They Want</h2>
          <p className="subtitle">
            Control who sees your features and when they see it.
          </p>
        </div>
        <div className="columns is-multiline features">
          <div className="column is-half is-mobile featureList-item columns is-mobile">
            <div className="column is-one-fifth has-text-right-tablet">
              <img src={actionTree} className="icon" alt="action Tree" />
            </div>
            <div className="column is-four-fifths">
              <h4>Control Your User Experience</h4>
              <p>
                You can easily control which users see what features, and when.
                The easy to use dashboard gives you granular control of your
                user's experience.
              </p>
              <p>
                Custom targeting rules target users based on any attribute like
                age, country, or email. You can use percentage rollouts to
                gradually release a feature to reduce risk and assess
                performance.
              </p>
            </div>
          </div>
          <div className="column is-half is-mobile featureList-item columns is-mobile">
            <div className="column is-one-fifth has-text-right-tablet">
              <img src={clock2} className="icon" alt="clock" />
            </div>
            <div className="column is-four-fifths">
              <h4>Release Features When You Want</h4>
              <p>
                Using feature management as a best practice, code deployments
                can occur separately from feature releases.
              </p>
              <p>
                Dev and ops teams can deploy code and keep new features hidden
                until product teams are ready to share. With LaunchDarkly you
                can easily turn your features on when you are ready.
              </p>
            </div>
          </div>
          <div className="column is-half is-mobile featureList-item columns is-mobile">
            <div className="column is-one-fifth has-text-right-tablet">
              <img src={feedback} className="icon" alt="feedback" />
            </div>
            <div className="column is-four-fifths">
              <h4>Get Feedback Easier</h4>
              <p>
                With comprehensive control over how your users experience new
                features, you can safely test in production and get valuable
                feedback.
              </p>
              <p>
                Feature management allows product teams to expose features
                independently and actively participate in the development cycle.
              </p>
            </div>
          </div>
          <div className="column is-half is-mobile featureList-item columns is-mobile">
            <div className="column is-one-fifth has-text-right-tablet">
              <img src={toggle5} className="icon" alt="toggle" />
            </div>
            <div className="column is-four-fifths">
              <h4>In Case of Emergency, Kill Switch</h4>
              <p>
                Anyone can instantly turn off any feature at any time. With
                feature flags, you don't have to roll code back to un-do a
                feature release.
              </p>
              <p>
                Get sleep at night knowing it's as easy as hitting the switch.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section className="section is-large bg-gradient">
      <div className="container content">
        <div className="columns is-centered is-multiline">
          <div className="column is-12 has-text-centered-tablet">
            <img
              className="blockquote-logo"
              src={appDirect}
              alt="AppDirect logo"
            />
          </div>
          <div className="column is-1-desktop is-1-tablet is-12-mobile">
            <img className="icon" src={quotes} alt="quotes" />
          </div>
          <div className="column is-7-desktop is-8-tablet is-12-mobile">
            <h2 className="title">
              A huge thing for us is risk. LaunchDarkly takes risk off the
              table.
            </h2>
            <svg className="stroke-10">
              <line x1="0" y1="0" x2="100%" y2="0" />
            </svg>
            <p className="text-large">
              Bryan Jowers, Product Manager, AppDirect
            </p>
            <div className="has-text-centered-tablet">
              <a href="/case-studies/appdirect/" className="cta">
                View Case Study
                <CtaArrow />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
    <ClosingCTAs title="Ready to See What Your Teams Can Do with Feature Management?" />
  </Layout>
)

export default Benefits
